import type { CSSProperties } from 'react';

import { classFor } from '../lib/class-map';
import { cn } from '../lib/cn';

import s from './Skeleton.module.css';

type Variant = 'block' | 'text' | 'circle';
type Radius = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface SkeletonProps {
  /** 'block' = a card/row plate, 'text' = one line of type, 'circle' = an avatar. */
  variant?: Variant;
  width?: number | string;
  height?: number | string;
  /** 'text' only — this many lines, the last one shorter. */
  lines?: number;
  radius?: Radius;
  className?: string;
}

const VARIANT_CLASS: Record<Variant, string> = {
  block: s.block ?? '',
  text: s.text ?? '',
  circle: s.circle ?? '',
};

const RADIUS_CLASS: Record<Radius, string> = {
  xs: s.radiusXs ?? '',
  sm: s.radiusSm ?? '',
  md: s.radiusMd ?? '',
  lg: s.radiusLg ?? '',
  xl: s.radiusXl ?? '',
};

function toSize(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
}

/**
 * A loading placeholder shaped like the content it stands in for.
 *
 * Hidden from assistive tech: the CALLER wraps a group of these in
 * `<div role="status" aria-label="Loading …">` once, so a screen reader hears
 * one "loading" rather than a plate per row. Sized inline because width and
 * height are data (the row it mirrors), not design tokens.
 */
export function Skeleton({
  variant = 'block',
  width,
  height,
  lines,
  radius,
  className,
}: SkeletonProps): JSX.Element {
  const style: CSSProperties = {};
  const w = toSize(width);
  const h = toSize(height);
  if (w !== undefined) style.width = w;
  if (h !== undefined) style.height = h;

  const classes = cn(
    s.root,
    classFor(VARIANT_CLASS, variant),
    radius !== undefined ? classFor(RADIUS_CLASS, radius) : undefined,
    className,
  );

  if (variant === 'text' && lines !== undefined && lines > 1) {
    return (
      <span className={s.lines} aria-hidden="true">
        {Array.from({ length: lines }, (_, index) => (
          <span
            key={index}
            className={cn(classes, index === lines - 1 ? s.lastLine : undefined)}
            style={style}
          />
        ))}
      </span>
    );
  }

  return <span className={classes} style={style} aria-hidden="true" />;
}
